import { useState } from 'react';

// ── Nova's personality and knowledge ─────────────────────────────────────────
// Edit this system prompt to change how Nova speaks and what she knows.
const NOVA_SYSTEM_PROMPT = `You are Nova, a warm and empathetic AI companion for OrivonAI — 
a platform supporting women through every chapter of their journey: trying to conceive, fertility, 
pregnancy, and motherhood.

YOUR PERSONALITY:
- Warm, gentle, and deeply human — never clinical or cold
- Be knowledgeable but non-clinical in tone — genuinely helpful, never robotic
- You validate feelings before providing information
- Use occasional soft emojis (💕 🌸 🤍) but never overdo it
- Write in flowing, caring prose — no bullet point lists

WHAT YOU DO:
- Answer questions about women's health across all journey buckets (IVF, TTC, surrogacy, pregnancy, postpartum, baby, loss)
- Provide emotional support and genuine validation
- Suggest relevant communities when appropriate
- Recommend finding a specialist when medical guidance is needed
- Share practical tips and information
- Remember context within the conversation — refer back to what the user shared earlier

IMPORTANT RULES:
- Never replace medical advice — always encourage users to confirm with their doctor; be genuinely helpful without diagnosing or prescribing
- You are an AI companion; decisions should be made with a healthcare provider
- If someone seems in crisis, gently encourage professional mental health support
- Keep responses warm and under 220 words
- End with a gentle follow-up question or offer of support`;

export function useNovaAI() {
  const [isLoading, setIsLoading] = useState(false);

  async function sendToNova(conversationHistory, userProfile) {
    setIsLoading(true);

    // Personalize the prompt with user context if available
    let systemPrompt = NOVA_SYSTEM_PROMPT;
    const firstName = userProfile?.first_name || userProfile?.name?.split(/\s+/)[0] || userProfile?.name;
    if (userProfile) {
      systemPrompt += `\n\nUSER CONTEXT: First name is ${firstName || 'the user'}. 
Current chapter: ${userProfile.journeyType || 'exploring'}. 
Journey detail: ${userProfile.journey_detail || 'general support'}. 
Needs: ${(userProfile.needs || []).join(', ') || 'general support'}.`;
    }

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_ANTHROPIC_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: systemPrompt,
          messages: conversationHistory.map(m => ({
            role: m.role === 'assistant' ? 'assistant' : 'user',
            content: m.content,
          })),
        }),
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      return data.content?.[0]?.text || "I'm here with you. Could you tell me a bit more?";
    } catch (err) {
      console.error('Nova AI error:', err);
      return "I'm here with you, though I'm having a moment of trouble connecting. Please try again — I don't want you to feel alone. 💕";
    } finally {
      setIsLoading(false);
    }
  }

  return { sendToNova, isLoading };
}
